import React, { useRef } from 'react';
import {
  SegmentedVideoPlayerInstance,
  SegmentedVideoPlayerProps,
} from './types';
import useBuffered from './useBuffered';
import useCurrentTime from './useCurrentTime';
import useDuration from './useDuration';
import useEnded from './useEnded';
import useEventListeners from './useEventListeners';
import useFrameAccurateSeek from './useFrameAccurateSeek';
import usePassthroughEventHandlers from './usePassthroughEventHandlers';
import useSeeked from './useSeeked';
import useSegmentedVideoPlayerInstance from './useSegmentedVideoPlayerInstance';
import { useTimeupdate } from './useTimeupdate';
import { createEventEmitter } from './utils';

/**
 * A video element that supports segmented sources.
 *
 * This player takes a video source and a list of segments and skips over gaps in
 * the source as defined by missing segments.  When seeking, during playback, etc.,
 * it should seem as if those segments of the video have been removed.
 */
const SegmentedVideoPlayer = React.forwardRef<
  SegmentedVideoPlayerInstance,
  SegmentedVideoPlayerProps
>(
  (
    {
      segments,
      timeupdateIntervalMillis = 100,
      onTimeupdate,
      onCanPlay,
      onCanPlayThrough,
      onEnded,
      ...props
    },
    ref,
  ) => {
    const emitter = useRef(createEventEmitter());

    const videoRef = useRef<HTMLVideoElement>();

    const { suppressNextSeeked } = useSeeked({
      emitter: emitter.current,
      videoRef,
    });

    const { getDurationMillis } = useDuration({
      segments,
    });

    const { setCurrentTime, getCurrentTime } = useCurrentTime({
      segments,
      videoRef,
    });

    useTimeupdate({
      emitter: emitter.current,
      videoRef,
      segments,
      timeupdateIntervalMillis,
      onSkipGap: suppressNextSeeked,
      onTimeupdate,
    });

    useEnded({
      emitter: emitter.current,
      getDurationMillis,
      videoRef,
      onEnded,
    });

    const { addEventListener, removeEventListener } = useEventListeners({
      emitter: emitter.current,
      videoRef,
    });

    const { getMaxBufferedPlaybackPosition } = useBuffered({
      segments,
      videoRef,
    });

    const seekToFrameAccurate = useFrameAccurateSeek({
      segments: segments ?? [],
      onSkipGap: suppressNextSeeked,
      videoRef,
    });

    useSegmentedVideoPlayerInstance({
      addEventListener,
      getDurationMillis,
      getCurrentTime,
      instanceRef: ref,
      maxBufferedPlaybackPosition: getMaxBufferedPlaybackPosition,
      removeEventListener,
      seekToFrameAccurate,
      setCurrentTime,
      videoRef,
    });

    usePassthroughEventHandlers({
      onCanPlay,
      onCanPlayThrough,
      videoRef,
    });

    return <video ref={videoRef} {...props} />;
  },
);

export default SegmentedVideoPlayer;
